import styled, { keyframes } from 'styled-components';
import React, { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch,useSelector,shallowEqual } from "react-redux";
import {Icon,Radio} from 'semantic-ui-react';
import ClickAwayListener from "react-click-away-listener";
import {
    SET_NEW_NAVIGATION
  } from "constants/actions";
import Filters from "./Filters";
import { usePageTranslations } from "hooks/usePageTranslations";
import { useLanguage } from "hooks/useLanguage";
  

const Navigation = ({ outcomes, core_data }) => {
  const t = usePageTranslations('app/audit/simple/Navigation/translations');
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const navigation = useSelector(
    (state) => state.audit?.new_navigation,
    shallowEqual
  );
  const [options1, setOptions1] = useState([]);
  const [options2, setOptions2] = useState([]);
  const [options3, setOptions3] = useState([]);
  const [options4, setOptions4] = useState([]);
  const [isOutcome, setIsOutcome] = useState(false);
  const [factorView, setFactorView] = useState(false);

  const { outcome, category, sub_category, factor } = navigation;

  // Generate the dropdown options based on outcomes and categories
  const generate_outcome_options = () => {
    let _options = [{ name: t('culture'), id: 0 }];
    const is_french = language === 'fr';
    outcomes?.questions.map((question) => {
      const questionName = is_french && question.fr_name && question.fr_name.length > 0 
        ? question.fr_name 
        : question.name;
      _options.push({
        name: questionName,
        id: question.id,
        type: "outcome",
      });
    });
    return _options;
  };

  const generate_category_options = () => {
    let _options = [];
    const is_french = language === 'fr';
    core_data?.categories?.[0]?.categories?.map((category) => {
      const categoryName = is_french && category.fr_name && category.fr_name.length > 0 
        ? category.fr_name 
        : category.name;
      _options.push({
        name: categoryName,
        id: category.id,
        type: "category",
        priority: category.priority,
      });
    });

    // Order the options so that the priority with the label primary is at the top
    _options = _options.sort((a, b) => {
      if (a.priority === "primary") {
        return -1;
      }
      if (b.priority === "primary") {
        return 1;
      }
      return 0;
    });

    return _options;
  };

  const generate_category_sub_options = () => {
    const is_french = language === 'fr';
    let _options = [
      { name: t('overview'), id: null, type: "cat_option" },
      { name: t('factorOverview'), id: 1, type: "cat_option" },
    ];
    core_data?.categories?.[0]?.categories
      .find((f) => f.id === category.id)
      ?.options.map((sub_category) => {
        const optionName = is_french && sub_category.fr && sub_category.fr.length > 0 
          ? sub_category.fr 
          : sub_category.name;
        _options.push({
          name: optionName,
          id: sub_category.id,
          type: "cat_option",
        });
      });
    return _options;
  };

  const generate_factor_options = () => {
    const is_french = language === 'fr';
    let _options = [{ name: t('overview'), id: null, type: "factor" }];
    core_data?.questions?.dimensions.map((dimension, d) => {
      dimension.factors.map((factor) => {
        const factorTitle = is_french 
          ? ((factor.fr_title && factor.fr_title.length > 0) ? factor.fr_title : (factor.fr && factor.fr.length > 0 ? factor.fr : factor.title))
          : factor.title;
        _options.push({
          name: factorTitle,
          id: factor.id,
          type: "factor",
          dimension: d,
        });
      });
    });
    return _options;
  };

  useEffect(() => {
    const newOptions = generate_outcome_options();
    setOptions1(newOptions);
    
    if (newOptions.length > 0) {
      // If no outcome is selected, select the first one (Culture)
      if (!outcome) {
        updateSelection({
          ...navigation,
          outcome: newOptions[0],
        });
      } else {
        // Update the selected outcome name to match the translated name if it exists
        const matchingOption = newOptions.find(opt => opt.id === outcome.id);
        if (matchingOption) {
          if (matchingOption.name !== outcome.name) {
            updateSelection({
              ...navigation,
              outcome: {
                ...outcome,
                name: matchingOption.name,
              },
            });
          }
        } else {
          // If selected outcome doesn't exist in options, select the first one
          updateSelection({
            ...navigation,
            outcome: newOptions[0],
          });
        }
      }
    }
  }, [outcomes, language, t, outcome?.id]);

  useEffect(() => {
    const newOptions = generate_category_options();
    setOptions2(newOptions);
    
    if (newOptions.length > 0) {
      // If no category is selected, select the first one (primary category)
      if (!category) {
        updateSelection({
          ...navigation,
          category: newOptions[0],
        });
      } else {
        // Update the selected category name to match the translated name if it exists
        const matchingOption = newOptions.find(opt => opt.id === category.id);
        if (matchingOption) {
          if (matchingOption.name !== category.name) {
            updateSelection({
              ...navigation,
              category: {
                ...category,
                name: matchingOption.name,
              },
            });
          }
        } else {
          // If selected category doesn't exist in options, select the first one
          updateSelection({
            ...navigation,
            category: newOptions[0],
          });
        }
      }
    }
  }, [core_data, language, t, category?.id]);

  useEffect(() => {
    if (category) {
      const newOptions = generate_category_sub_options();
      setOptions3(newOptions);
      
      // Update the selected sub_category name to match the translated name if it exists
      if (sub_category && newOptions.length > 0) {
        const matchingOption = newOptions.find(opt => opt.id === sub_category.id);
        if (matchingOption && matchingOption.name !== sub_category.name) {
          updateSelection({
            ...navigation,
            sub_category: {
              ...sub_category,
              name: matchingOption.name,
            },
          });
        } else if (!matchingOption) {
          // If no match found, reset sub_category
          updateSelection({ ...navigation, factor: null, sub_category: null });
        }
      } else {
        updateSelection({ ...navigation, factor: null, sub_category: null });
      }
    }
  }, [category, language, t, sub_category?.id]);

  useEffect(() => {
    if (sub_category?.name == t('overview')) {
      setOptions4([]);
      updateSelection({ ...navigation, factor: null });
      if (factorView) {
        setFactorView(false);
      }
    } else {
      if (!factorView) {
        setFactorView(true);
      }
    }
  }, [sub_category, t]);

  useEffect(() => {
    if (factor?.name == t('overview')) {
      setOptions4([]);
      return;
    }
    
    if (factor) {
      const newOptions = generate_factor_options();
      setOptions4(newOptions);
      
      // Update the selected factor name to match the translated name if it exists
      if (newOptions.length > 0) {
        const matchingOption = newOptions.find(opt => opt.id === factor.id && opt.dimension === factor.dimension);
        if (matchingOption && matchingOption.name !== factor.name) {
          updateSelection({
            ...navigation,
            factor: {
              ...factor,
              name: matchingOption.name,
            },
          });
        }
      }
    }
  }, [factor?.id, factor?.dimension, language, t]);

  useEffect(() => {
    if (outcome?.name !== t('culture')) {
      setIsOutcome(true);
      updateSelection({ ...navigation, factor: null, sub_category: null });
    } else {
      setIsOutcome(false);
    }
  }, [outcome, t]);

  const selectFactor = (value) => {
    setFactorView(value);

    if (!value) {
      updateSelection({
        ...navigation,
        factor: null,
        sub_category: { name: t('overview'), id: null, type: "cat_option" },
      });
    } else {
      updateSelection({
        ...navigation,
        sub_category: { name: t('factorOverview'), id: 1, type: "cat_option" },
      });
    }
  };

  const goBack = () => {
    if (navigation?.outcome?.type === "outcome") {
      if (navigation?.category?.priority === "primary" && options4?.length === 0) {
        const primaryCategory =
          navigation?.category || options2?.[0] || null;
        const defaultSelection = {
          selected1: null,
          selected2: null,
          selected3: null,
          factor: null,
          outcome: {
            name: t('culture'),
            id: 0,
          },
        };

        if (primaryCategory) {
          defaultSelection.category = primaryCategory;
        }

        return updateSelection(defaultSelection);
      }

      if (navigation?.category?.priority !== "primary" && options4?.length === 0) {
        // Get the primary category information
        let primary = core_data?.categories?.[0]?.categories.find(
          (f) => f.priority === "primary"
        );

        return updateSelection({
          ...navigation,
          category: {
            name: primary.name,
            id: primary.id,
            type: "category",
            priority: primary.priority,
          },
        });
      }

      if (options4?.length > 0) {
        updateSelection({ ...navigation, factor: null });
        return setOptions4([]);
      }
    } else if (options4.length > 0) {
      updateSelection({ ...navigation, factor: null });
      return setOptions4([]);
    }
    if (factorView) {
      selectFactor(false);
    } else if (sub_category?.name == t('factorOverview')) {
      updateSelection({
        ...navigation,
        sub_category: { name: t('overview'), id: null, type: "cat_option" },
      });
    } else if (sub_category?.name == t('overview')) {
      updateSelection({ ...navigation, category: null });
    }
  };
  // Push out the selected dropdown options to the redux store

  // Action creators (assuming these are properly defined elsewhere)
  const updateSelection = (selection) =>
    dispatch({
      type: SET_NEW_NAVIGATION,
      payload: selection,
    });

  return (
    <>
      <Row>
        <Container>
          <Dropdown
            options={options1}
            selected={outcome}
            label={t('dataView')}
            setSelected={(option) =>
              updateSelection({ ...navigation, outcome: option })
            }
          />

          <Dropdown
            options={options2}
            selected={category}
            label={t('categoryView')}
            setSelected={(option) =>
              updateSelection({ ...navigation, category: option })
            }
          />

          {!isOutcome && (
            <>
              <Dropdown
                options={options3}
                selected={sub_category}
                label={t('selectedCategory')}
                setSelected={(option) =>
                  updateSelection({ ...navigation, sub_category: option })
                }
              />
            </>
          )}
          {options4.length > 0 && (
            <>
              <Dropdown
                options={options4}
                selected={factor}
                label={t('selectedFactor')}
                style={{fontSize:12}}
                setSelected={(option) =>
                  updateSelection({ ...navigation, factor: option })
                }
              />
            </>
          )}
        </Container>
        {!isOutcome && (
          <Radios>
            {/* <Radio
            label='Category Comparison'
            name='radioGroup'
            value={1}
            checked={factorView == false}
            onChange={()=>selectFactor(false)}
            style={{marginRight:10}}
          /> */}
            <Radio
              label={t('factorBreakdown')}
              name="radioGroup"
              value={2}
              toggle
              checked={factorView === true}
              onChange={() => selectFactor(!factorView)}
            />
          </Radios>
        )}
      </Row>
      <Row2 align={sub_category?.name !== t('overview')}>
        {sub_category?.name !== t('overview') && (
          <BackButton onClick={() => goBack()}>
            <Icon name="chevron left" style={{ marginTop: -7 }} />
            {t('back')}
          </BackButton>
        )}
        <Filters />
      </Row2>
    </>
  );
};

export default Navigation


const Dropdown = ({options,selected,setSelected,label}) =>{
    const t = usePageTranslations('app/audit/simple/Navigation/translations');
    const [open, setOpen] = useState(false);
    const [hover, setHover] = useState(false);


    useEffect(()=>{
        if (options.length === 0) return;
        
        if(!selected){
            // If nothing selected, select first option
            setSelected(options[0]);
        } else {
            // Find matching option by ID to ensure selected name matches translated name
            const matchingOption = options.find(opt => {
                // Match by ID, and also check dimension for factors
                if (opt.dimension !== undefined && selected.dimension !== undefined) {
                    return opt.id === selected.id && opt.dimension === selected.dimension;
                }
                return opt.id === selected.id;
            });
            
            if (matchingOption) {
                // Update if name doesn't match (translation changed)
                if (matchingOption.name !== selected.name) {
                    setSelected(matchingOption);
                }
            } else {
                // If no match found by ID, select first option
                setSelected(options[0]);
            }
        }
    },[options, selected?.id, selected?.dimension])

    const selectOption = (option) =>{
        setOpen(false)
        setSelected(option)
    }



    return (
      <Box>
      <Label>{label}</Label>
      <ClickAwayListener onClickAway={()=>setOpen(false)}>
            <DropdownContainer onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}>
                <Nav onClick={()=>setOpen(!open)}>
              <Selection>{selected?.name === t('culture') ? t('categories') : selected?.name}</Selection>
                    <Icon name="triangle down" style={{marginTop:-4,color:'#B3B3B3'}}/>
                </Nav>
                {open && <DropdownContent>
              {options.map((option, i) => <Option key={i} onClick={() => selectOption(option)}>{option.name === t('culture') ? t('categories') : option.name}</Option>)}
                </DropdownContent>}
            </DropdownContainer>
        </ClickAwayListener>
      </Box>
        
    )
}


const Container = styled.div`
    display:flex;
    font-family:'raleway';
    margin-bottom:20px;
`

const DropdownContainer = styled.div`
    position:relative;
    border:1px solid lightgrey;
    padding:5px;
`

const Nav = styled.div`
    display:flex;
    align-items:center;
    cursor:pointer;
`

const Selection = styled.div`
    font-size:10px;
    color:#2A3039;
    margin-right:5px;
`

const DropdownContent = styled.div`
    position:absolute;
    top:40px;
    left:0;
    background-color:white;
    box-shadow:0px 0px 10px 0px rgba(0,0,0,0.1);
    border-radius:5px;
    z-index:100;
    width:200px;
`

const Option = styled.div`
    padding:10px 20px;
    font-size:14px;
    color:#2A3039;
    cursor:pointer;

    &:hover{
        background-color:#F5F5F5;
    }
`

const Slash = styled.div`
    font-size:20px;
    color:#2A3039;
    margin-right:10px;
    color:#DFDFDF;
`

const Label = styled.div`
    font-size:12px;
    color:#2A3039;
    margin-right:10px;
`

const Box = styled.div`
    display:flex;
    flex-direction:column;
    margin-right:10px;
`

const Radios = styled.div`
    display:flex;
    margin-bottom:10px;

`
const Row = styled.div`
    display:flex;
    justify-content:space-between;
    align-items:center;
    margin-bottom:0px;
`

const BackButton = styled.div`
    font-size:14px;
    color:#B3B3B3;
    cursor:pointer;
    display:flex;
    align-items:center;
    font-size:12px;
`

const Row2 = styled.div`
    display:flex;
    justify-content:${props=>props.align?'space-between':'flex-end'};
    align-items:center;
`
